// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import i18n from "i18next";
import * as Conf from "./Conf";
import {initReactI18next} from "react-i18next";
import en from "./locales/en/data.json";

const resourcesToBackend = (res) => ({
  type: "backend",
  init(services, backendOptions, i18nextOptions) {/* use services and options */},
  read(language, namespace, callback) {
    // 统一将语言代码转换为小写，确保与文件系统路径匹配
    // 例如：zh-HK -> zh-hk, zh-CN -> zh-cn
    const normalizedLanguage = language.toLowerCase();

    if (typeof res === "function") {
      if (res.length < 3) {
        try {
          const r = res(normalizedLanguage, namespace);
          if (r && typeof r.then === "function") {
            r.then((data) => {
              // res 函数已经返回了 res.default[namespace]，所以 data 直接是 namespace 对象
              // 例如：{ "Logout": "登出", "My Account": "我的帳戶", ... }
              if (data && typeof data === "object" && Object.keys(data).length > 0) {
                callback(null, data);
              } else {
                // namespace 不存在或为空（如 translation），使用 fallback 机制
                // i18next 会自动使用 fallbackLng 配置
                // 不输出警告，因为这是正常情况（translation namespace 不存在）
                if (namespace !== "translation") {
                  // eslint-disable-next-line no-console
                  console.warn(`Language resource not found: ${normalizedLanguage}/${namespace}, will use fallback`);
                }
                callback(null, null);  // 返回 null 让 i18next 使用 fallback
              }
            }).catch((err) => {
              // eslint-disable-next-line no-console
              console.error(`Failed to load language: ${normalizedLanguage}/${namespace}`, err);
              callback(err, null);
            });
          } else {
            callback(null, r);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Error loading language: ${normalizedLanguage}/${namespace}`, err);
          callback(err, null);
        }
        return;
      }
      res(normalizedLanguage, namespace, callback);
      return;
    }
    callback(null, res && res[normalizedLanguage] && res[normalizedLanguage][namespace]);
  },
});

function initLanguage() {
  let language = localStorage.getItem("language");
  if (language === undefined || language === null || language === "") {
    if (Conf.ForceLanguage !== "") {
      language = Conf.ForceLanguage;
    } else {
      const userLanguage = navigator.language;
      switch (userLanguage) {
      case "en":
        language = "en";
        break;
      case "en-US":
        language = "en";
        break;
      case "zh-CN":
        language = "zh";
        break;
      case "zh":
        language = "zh";
        break;
      case "zh-HK":
      case "zh-HK-HK":
        language = "zh-hk";
        break;
      case "es":
        language = "es";
        break;
      case "fr":
        language = "fr";
        break;
      case "de":
        language = "de";
        break;
      case "id":
        language = "id";
        break;
      case "ja":
        language = "ja";
        break;
      case "ko":
        language = "ko";
        break;
      case "ru":
        language = "ru";
        break;
      case "vi":
        language = "vi";
        break;
      case "pt":
        language = "pt";
        break;
      case "it":
        language = "it";
        break;
      case "ms":
        language = "ms";
        break;
      case "tr":
        language = "tr";
        break;
      case "ar":
        language = "ar";
        break;
      case "he":
        language = "he";
        break;
      case "nl":
        language = "nl";
        break;
      case "pl":
        language = "pl";
        break;
      case "fi":
        language = "fi";
        break;
      case "sv":
        language = "sv";
        break;
      case "uk":
        language = "uk";
        break;
      case "kk":
        language = "kk";
        break;
      case "fa":
        language = "fa";
        break;
      case "cs":
      case "cs-CZ":
        language = "cs";
        break;
      case "sk":
      case "sk-SK":
        language = "sk";
        break;
      case "az":
        language = "az";
        break;
      default:
        language = Conf.DefaultLanguage;
      }
    }
  }

  return language;
}

i18n.use(resourcesToBackend(async(language, namespace) => {
  try {
    // 统一将语言代码转换为小写，确保与文件系统路径匹配
    // 例如：zh-HK -> zh-hk, zh-CN -> zh-cn
    const normalizedLanguage = language.toLowerCase();

    // 处理不存在的 namespace（如 translation），返回空对象而不是抛出错误
    // 这样 i18next 会使用 fallback 机制，而不会报错
    if (namespace === "translation" || !namespace) {
      return {};
    }

    // 使用 webpack magic comments 确保每个语言文件单独打包
    // 这样可以避免 webpack 错误地将不同语言文件合并
    const res = await import(
      /* webpackChunkName: "locale-[request]" */
      /* webpackMode: "lazy" */
      `./locales/${normalizedLanguage}/data.json`
    );
    if (res && res.default && res.default[namespace]) {
      return res.default[namespace];
    } else {
      // namespace 不存在，返回空对象让 i18next 使用 fallback
      // 不抛出错误，避免控制台报错
      return {};
    }
  } catch (err) {
    // 导入失败时，返回空对象而不是抛出错误
    // 这样 i18next 会使用 fallback 机制
    // eslint-disable-next-line no-console
    console.warn(`Failed to load language file: ./locales/${language.toLowerCase()}/data.json, will use fallback`, err);
    return {};
  }
}
))
  .use(initReactI18next)
  .init({
    lng: initLanguage(),
    ns: Object.keys(en),
    defaultNS: Object.keys(en)[0] || false,  // 使用第一个实际的 namespace 作为默认值，避免使用 translation
    fallbackLng: {
      "zh-hk": ["zh", "en"],  // zh-hk 失败时，先尝试 zh，再尝试 en
      "default": ["en"],
    },

    keySeparator: false,

    interpolation: {
      escapeValue: true,
    },
    // debug: true,  // 取消注释以启用调试模式
    saveMissing: true,
  });
export default i18n;
